import BarChart, { IBar } from "@components/barChart";
import { getDetailedDueCountsFromKnols } from "@lib/fsrs/dueCounts";
import { Deck, IDeck } from "@models/deck";
import { isDeckFSRS } from "@models/deckSettings";
import { IKnol } from "@models/knol";
import useErrorAlert from "hooks/util/useErrorAlert";
import L10n from "localization";
import { useCallback, useMemo } from "react";
import Style from "style";

interface IProps {
  deck: IDeck | undefined;
  knols?: Iterable<IKnol>;
  filteredKnols?: Iterable<IKnol>;
  height?: string;
  selectedDueDates?: string[];
}

export default function FSRSHistogram(props: IProps): JSX.Element {
  const { deck, height, selectedDueDates } = props;

  // Check if deck is in FSRS mode - only show for FSRS
  const isFSRS = isDeckFSRS(deck);

  if (!isFSRS) {
    return <></>;
  }

  const { filteredCounts, allCounts } = useMemo(() => {
    const filteredCounts = props.filteredKnols
      ? getDetailedDueCountsFromKnols(props.filteredKnols, deck)
      : null;

    const allCounts = props.knols ? getDetailedDueCountsFromKnols(props.knols, deck) : null;

    return {
      filteredCounts: filteredCounts || {
        new: 0,
        now: 0,
        within24hr: 0,
        tomorrow: 0,
        within1wk: 0,
        future: 0,
      },
      allCounts: allCounts || {
        new: 0,
        now: 0,
        within24hr: 0,
        tomorrow: 0,
        within1wk: 0,
        future: 0,
      },
    };
  }, [props.filteredKnols, props.knols, deck]);

  const [showToggleDueDateError] = useErrorAlert({ code: "TOGGLING_REVIEW_GRADE" });

  const toggleSelected = useCallback(
    (dueDate: string) => {
      const selectedDueDatesSet = new Set(selectedDueDates);
      if (selectedDueDatesSet.has(dueDate)) {
        selectedDueDatesSet.delete(dueDate);
      } else {
        selectedDueDatesSet.add(dueDate);
      }
      Deck.setSetting(deck, "fsrsDueDateFilter", Array.from(selectedDueDatesSet)).catch(
        (err: Error) => {
          showToggleDueDateError(err);
        },
      );
    },
    [deck, selectedDueDates, showToggleDueDateError],
  );

  const bars: IBar[] = [
    {
      class: "NEW",
      count: filteredCounts.new,
      shadowCount: allCounts.new,
      label: L10n.localize((s) => s.fsrs.stateNew),
      onClick: () => toggleSelected("new"),
      highlighted: selectedDueDates?.includes("new"),
    },
    {
      class: "F",
      count: filteredCounts.now,
      shadowCount: allCounts.now,
      label: L10n.localize((s) => s.fsrs.dueNow),
      onClick: () => toggleSelected("now"),
      highlighted: selectedDueDates?.includes("now"),
    },
    {
      class: "D",
      count: filteredCounts.within24hr,
      shadowCount: allCounts.within24hr,
      label: L10n.localize((s) => s.fsrs.dueWithin24hr),
      onClick: () => toggleSelected("within24hr"),
      highlighted: selectedDueDates?.includes("within24hr"),
    },
    {
      class: "C",
      count: filteredCounts.tomorrow,
      shadowCount: allCounts.tomorrow,
      label: L10n.localize((s) => s.general.tomorrow),
      onClick: () => toggleSelected("tomorrow"),
      highlighted: selectedDueDates?.includes("tomorrow"),
    },
    {
      class: "B",
      count: filteredCounts.within1wk,
      shadowCount: allCounts.within1wk,
      label: L10n.localize((s) => s.fsrs.dueWithin1wk),
      onClick: () => toggleSelected("within1wk"),
      highlighted: selectedDueDates?.includes("within1wk"),
    },
    {
      class: "A",
      count: filteredCounts.future,
      shadowCount: allCounts.future,
      label: L10n.localize((s) => s.general.future),
      onClick: () => toggleSelected("future"),
      highlighted: selectedDueDates?.includes("future"),
    },
  ];

  return (
    <div
      style={{
        paddingLeft: Style.edgePadding,
        paddingRight: Style.edgePadding,
        height: "100%",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <BarChart
        height={height ? `calc(${height} - 0.5rem)` : "calc(100% - 0.5rem)"}
        bars={bars}
        count={true}
        grow={true}
      />
    </div>
  );
}
